package com.daytimegaming.ourdailyfinances.data.network.dto

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

@Serializable
data class CounterpartyDto(
    @SerialName("name") val name: String,
    @SerialName("type") val type: String,
    @SerialName("website") val website: String? = null,
    @SerialName("logo_url") val logoUrl: String? = null,
    @SerialName("entity_id") val entityId: String? = null,
    @SerialName("confidence_level") val confidenceLevel: String? = null,
)

@Serializable
data class LocationDto(
    @SerialName("address") val address: String? = null,
    @SerialName("city") val city: String? = null,
    @SerialName("region") val region: String? = null,
    @SerialName("postal_code") val postalCode: String? = null,
    @SerialName("country") val country: String? = null,
    @SerialName("lat") val lat: Double? = null,
    @SerialName("lon") val lon: Double? = null,
    @SerialName("store_number") val storeNumber: String? = null,
)

@Serializable
data class PaymentMetaDto(
    @SerialName("reference_number") val referenceNumber: String? = null,
    @SerialName("ppd_id") val ppdId: String? = null,
    @SerialName("payee") val payee: String? = null,
    @SerialName("by_order_of") val byOrderOf: String? = null,
    @SerialName("payer") val payer: String? = null,
    @SerialName("payment_method") val paymentMethod: String? = null,
    @SerialName("payment_processor") val paymentProcessor: String? = null,
    @SerialName("reason") val reason: String? = null,
)

@Serializable
data class PersonalFinanceCategoryDto(
    @SerialName("primary") val primary: String,
    @SerialName("detailed") val detailed: String,
    @SerialName("confidence_level") val confidenceLevel: String? = null,
    @SerialName("version") val version: String? = null,
)

@Serializable
data class TransactionDto(
    @SerialName("transaction_id") val transactionId: String,
    @SerialName("account_id") val accountId: String,
    @SerialName("amount") val amount: Double,
    @SerialName("date") val date: String,
    @SerialName("name") val name: String,
    @SerialName("merchant_name") val merchantName: String? = null,
    @SerialName("category") val category: List<String>,
    @SerialName("pending") val pending: Boolean,
    @SerialName("iso_currency_code") val isoCurrencyCode: String? = null,
    @SerialName("logo_url") val logoUrl: String? = null,
    @SerialName("website") val website: String? = null,
    @SerialName("personal_finance_category_icon_url") val personalFinanceCategoryIconUrl: String? = null,
    @SerialName("counterparties") val counterparties: List<CounterpartyDto>? = null,
    @SerialName("location") val location: LocationDto? = null,
    @SerialName("payment_meta") val paymentMeta: PaymentMetaDto? = null,
    @SerialName("personal_finance_category") val personalFinanceCategory: PersonalFinanceCategoryDto? = null,
    @SerialName("user_category_override") val userCategoryOverride: PersonalFinanceCategoryDto? = null,
)

@Serializable
data class TransactionsResponse(
    @SerialName("transactions") val transactions: List<TransactionDto>,
    @SerialName("investments") val investments: InvestmentsDto? = null,
    @SerialName("prices_syncing") val pricesSyncing: Boolean? = null,
)

@Serializable
data class UpdateCategoryRequest(
    @SerialName("detailed") val detailed: String?
)

@Serializable
data class OptionContractDto(
    @SerialName("contract_type") val contractType: String,
    @SerialName("expiration_date") val expirationDate: String,
    @SerialName("strike_price") val strikePrice: Double,
    @SerialName("underlying_security_ticker") val underlyingSecurityTicker: String,
)

@Serializable
data class YieldRateDto(
    @SerialName("percentage") val percentage: Double,
    @SerialName("type") val type: String,
)

@Serializable
data class FixedIncomeDto(
    @SerialName("maturity_date") val maturityDate: String? = null,
    @SerialName("issue_date") val issueDate: String? = null,
    @SerialName("face_value") val faceValue: Double? = null,
    @SerialName("yield_rate") val yieldRate: YieldRateDto? = null,
)

@Serializable
data class SecurityDto(
    @SerialName("security_id") val securityId: String,
    @SerialName("name") val name: String? = null,
    @SerialName("ticker_symbol") val tickerSymbol: String? = null,
    @SerialName("type") val type: String? = null,
    @SerialName("subtype") val subtype: String? = null,
    @SerialName("isin") val isin: String? = null,
    @SerialName("cusip") val cusip: String? = null,
    @SerialName("sedol") val sedol: String? = null,
    @SerialName("institution_security_id") val institutionSecurityId: String? = null,
    @SerialName("institution_id") val institutionId: String? = null,
    @SerialName("proxy_security_id") val proxySecurityId: String? = null,
    @SerialName("is_cash_equivalent") val isCashEquivalent: Boolean? = null,
    @SerialName("close_price") val closePrice: Double? = null,
    @SerialName("close_price_as_of") val closePriceAsOf: String? = null,
    @SerialName("iso_currency_code") val isoCurrencyCode: String? = null,
    @SerialName("unofficial_currency_code") val unofficialCurrencyCode: String? = null,
    @SerialName("market_identifier_code") val marketIdentifierCode: String? = null,
    @SerialName("sector") val sector: String? = null,
    @SerialName("industry") val industry: String? = null,
    @SerialName("cfi_code") val cfiCode: String? = null,
    @SerialName("update_datetime") val updateDatetime: String? = null,
    @SerialName("option_contract") val optionContract: OptionContractDto? = null,
    @SerialName("fixed_income") val fixedIncome: FixedIncomeDto? = null,
)

@Serializable
data class HoldingDto(
    @SerialName("account_id") val accountId: String,
    @SerialName("security_id") val securityId: String,
    @SerialName("security") val security: SecurityDto? = null,
    @SerialName("proxy_security") val proxySecurity: SecurityDto? = null,
    @SerialName("institution_price") val institutionPrice: Double,
    @SerialName("institution_price_as_of") val institutionPriceAsOf: String? = null,
    @SerialName("institution_value") val institutionValue: Double? = null,
    @SerialName("cost_basis") val costBasis: Double? = null,
    @SerialName("quantity") val quantity: Double,
    @SerialName("iso_currency_code") val isoCurrencyCode: String? = null,
)

@Serializable
data class InvestmentTransactionDto(
    @SerialName("investment_transaction_id") val investmentTransactionId: String,
    @SerialName("account_id") val accountId: String,
    @SerialName("security_id") val securityId: String? = null,
    @SerialName("security") val security: SecurityDto? = null,
    @SerialName("date") val date: String,
    @SerialName("name") val name: String,
    @SerialName("quantity") val quantity: Double? = null,
    @SerialName("amount") val amount: Double,
    @SerialName("price") val price: Double? = null,
    @SerialName("fees") val fees: Double? = null,
    @SerialName("type") val type: String,
    @SerialName("subtype") val subtype: String? = null,
    @SerialName("iso_currency_code") val isoCurrencyCode: String? = null,
    @SerialName("cancel_transaction_id") val cancelTransactionId: String? = null,
)

@Serializable
data class InvestmentsDto(
    @SerialName("holdings") val holdings: List<HoldingDto>,
    @SerialName("transactions") val transactions: List<InvestmentTransactionDto>,
)
